import { createAsyncThunk, createEntityAdapter, createSlice, current } from '@reduxjs/toolkit';
import { Conversation, JSONApiDataObject, ReduxEntity } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import normalize from 'json-api-normalizer';
import { RootState } from 'Douleutaras/redux/store';
import { usersSlice } from 'Douleutaras/redux/data/users/usersSlice';
import { categoriesSlice } from 'Douleutaras/redux/data/categories/categoriesSlice';
import { jobsSlice } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { jobBidsBookNowSlice } from 'Douleutaras/redux/data/jobBids/jobBidsBookNowSlice';
import { jobBidsSlice } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import { jobMunicipalitySlice } from 'Douleutaras/redux/data/jobMunicipality/jobMunicipalitySlice';
import { deepMergeMetaData } from 'Douleutaras/utilities/redux/merge';
import { jobBidsStatusBuyerSlice } from 'Douleutaras/redux/data/jobBids/jobBidsStatusBuyerSlice';

export const conversationsAdapter = createEntityAdapter<JSONApiDataObject<Conversation> & ConversationOptions>({
  selectId: job => job.id
});

export const conversationsEndpoint = 'api/public/v3/conversation/';
export const singleConversationEndpoint = id => `${conversationsEndpoint}${id}/`;

const handleConversationResponse = ({ response, endpoint, api }) => {
  const normalizedResponse = normalize(response, { endpoint });
  const { usersAdded } = usersSlice.actions;
  const { bidBuyerStatusAdded } = jobBidsStatusBuyerSlice.actions;
  const { categoriesAdded } = categoriesSlice.actions;
  const { jobsAdded } = jobsSlice.actions;
  const { jobBidsBookNowAdded } = jobBidsBookNowSlice.actions;
  const { jobBidsAdded } = jobBidsSlice.actions;
  const { jobMunicipalityAdded } = jobMunicipalitySlice.actions;

  if (normalizedResponse.users) {
    api.dispatch(
      usersAdded(normalizedResponse.users)
    );
  }

  if (normalizedResponse.categories) {
    api.dispatch(
      categoriesAdded(normalizedResponse.categories)
    );
  }

  if (normalizedResponse.jobs) {
    api.dispatch(
      jobsAdded(normalizedResponse.jobs)
    );
  }

  if (normalizedResponse.bookNowEntries) {
    api.dispatch(
      jobBidsBookNowAdded(normalizedResponse.bookNowEntries)
    );
  }

  if (normalizedResponse.municipality) {
    api.dispatch(
      jobMunicipalityAdded(normalizedResponse.municipality)
    );
  }

  if (typeof normalizedResponse.bids !== 'undefined') {
    api.dispatch(
      jobBidsAdded(normalizedResponse.bids)
    );
  }

  if (normalizedResponse.bidStatusBuyerUpdate) {
    api.dispatch(
      bidBuyerStatusAdded(normalizedResponse.bidStatusBuyerUpdate)
    );
  }

  return normalizedResponse;
};

export const fetchConversationsOptions = createAsyncThunk(
  'conversations/getOptions',
  async ({ includes }: { includes?: Array<string> }, api) => {
    const response = await makeRequest(conversationsEndpoint,
      {
        includes,
        method: 'OPTIONS',
        additionalGetParams: {}
      })(api);

    return {
      unseenConversationsCount: response.data.data.unseenConversationsCount,
      unseenBuyerConversationsCount: response.data.data.unseenBuyerConversationsCount,
      unseenSellerConversationsCount: response.data.data.unseenSellerConversationsCount,
      chatEnabled: response.data.data.chatEnabled
    };
  }
);

export const fetchConversations = createAsyncThunk(
  'conversations/getAll',
  async ({ includes, getParams, page = 1 }: { includes?: Array<string>, getParams?: {}, page?: number },
         api) => {

    const response = await makeRequest(conversationsEndpoint, {
      includes,
      method: 'GET',
      additionalGetParams: { ...getParams,
        'page[number]': page,
        'page[size]': 10
      }
    })(api);

    return handleConversationResponse({ response: response.data, endpoint: conversationsEndpoint, api });
  }
);

export const fetchSingleConversation = createAsyncThunk(
  'conversations/getOne',
  async ({ conversationId, includes }: { conversationId: string | number, includes?: Array<string> }, api) => {

    const response = await makeRequest(singleConversationEndpoint(conversationId), {
      includes,
      method: 'GET'
    })(api);

    return { ...handleConversationResponse({ response: response.data, endpoint: conversationsEndpoint, api })
      , conversationId };
  }
);

export const markConversationAsSeen = createAsyncThunk(
  'conversation/markAsSeen',
  async ({ conversationId }: { conversationId: string }, api) => {
    const { clearConversationSeenCount } = conversationsSlice.actions;
    const response = await makeRequest(singleConversationEndpoint(conversationId), {
      method: 'PATCH',
      body: {
        data: {
          type: 'conversations',
          id: conversationId,
          attributes: {
            seen: true
          }
        }
      }
    })(api);

    api.dispatch(
      clearConversationSeenCount({ conversationId })
    );
  }
);

export interface ConversationOptions {
  unseenConversationsCount: number;
  unseenBuyerConversationsCount: number;
  unseenSellerConversationsCount: number;
  chatEnabled: boolean;
}

export const conversationsSlice = createSlice({
  name: 'conversations',
  initialState: conversationsAdapter.getInitialState({
    loading: undefined,
    error: false,
    meta: {},
    entities: {},
    unseenConversationsCount: 0,
    unseenBuyerConversationsCount: 0,
    unseenSellerConversationsCount: 0,
    chatEnabled: false
  } as ReduxEntity<JSONApiDataObject<Conversation>> & ConversationOptions),
  reducers: {
    conversationsAdded: (state, action) => {
      conversationsAdapter.setMany(state, action.payload);
    },
    lastMessageAdded: (state, action) => {
      const conversationId = action.payload.lastMessage.conversationId;
      const metaEndpoint = conversationsEndpoint;

      if (state.meta[metaEndpoint] === undefined) {
        state.meta[metaEndpoint] = {
          meta: {}
        };
      }
      if (state.meta[metaEndpoint].meta.lastMessages === undefined) {
        state.meta[metaEndpoint].meta.lastMessages = {};
      }
      if (state.meta[metaEndpoint].meta.lastMessages[conversationId] === undefined) {
        state.meta[metaEndpoint].meta.lastMessages[conversationId] = {};
      }
      state.meta[metaEndpoint].meta.lastMessages[conversationId].content
        = action.payload.lastMessage.content;
    },
    // Accepts conversation id as a payload. Then, it will bumpb the conversation to the top (of the array)
    // so its displayed on the top of the list
    bumpConversationToTop: (state, action: { payload: { conversationId: number } }) => {
      const conversationId = action.payload.conversationId;
      const metaEndpoint = conversationsEndpoint;
      // Finds the provided conversation id in meta object, removes it and pushes it to the top
      const index = state.meta[metaEndpoint].data
        .findIndex(conv => conv.id === String(conversationId));

      const conversation = state.meta[metaEndpoint].data.splice(index, 1)[0];
      state.meta[metaEndpoint].data.unshift(conversation);
    },
    // Update timestamp of "lastMessageAt" to current
    updateConversationLastMessageTimestamp: (state, action: { payload: { conversationId: number } }) => {
      state.entities[action.payload.conversationId].attributes.lastMessageAt = new Date().toISOString();
    },
    clearConversationSeenCount: (state, action: { payload: { conversationId: string } }) => {
      const conversationId = action.payload.conversationId;
      if (state.meta[conversationsEndpoint] === undefined) {
        state.meta[conversationsEndpoint] = {
          meta: {
            unseenMessageCounts: {}
          }
        };
      }
      state.meta[conversationsEndpoint].meta.unseenMessageCounts[conversationId] = 0;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchSingleConversation.fulfilled, (state, action) => {
      const conversationId = action.payload.conversationId;
      conversationsAdapter.upsertMany(state, action.payload.conversations || {});

      // If meta object is empty, populate it with skeleton object
      if (state.meta[conversationsEndpoint] === undefined) {
        state.meta[conversationsEndpoint] = {
          meta: {
            unseenMessageCounts: {},
            lastMessages: {}
          },
          data: []
        };
      }

      if (state.meta[conversationsEndpoint].meta.lastMessages === undefined) {
        state.meta[conversationsEndpoint].meta.lastMessages = {};
      }

      if (state.meta[conversationsEndpoint].meta.unseenMessageCounts === undefined) {
        state.meta[conversationsEndpoint].meta.unseenMessageCounts = {};
      }

      // Merge data object
      // @ts-ignore
      state = deepMergeMetaData(state, action, conversationsEndpoint, true);

      // Edit lastMessages object inside meta and merge it with "lastMessage".
      // Notice that "lastMessage" is singular and its coming from API endpoint like it is
      state.meta[conversationsEndpoint].meta.lastMessages[conversationId] =
        action.payload.meta[conversationsEndpoint].meta?.lastMessage;

      // Same as above for unseenMessageCount(s)
      state.meta[conversationsEndpoint].meta.unseenMessageCounts[conversationId] =
        action.payload.meta[conversationsEndpoint].meta.unseenMessageCount;
    });
    builder.addCase(fetchConversationsOptions.fulfilled, (state, action) => {
      state.unseenConversationsCount = action.payload.unseenConversationsCount;
      state.unseenBuyerConversationsCount = action.payload.unseenBuyerConversationsCount;
      state.unseenSellerConversationsCount = action.payload.unseenSellerConversationsCount;
      state.chatEnabled = action.payload.chatEnabled;
    });
    builder.addCase(fetchConversations.fulfilled, (state, action) => {
      conversationsAdapter.upsertMany(state, action.payload.conversations || {});

      // @ts-ignore
      state = deepMergeMetaData(state, action, conversationsEndpoint);

      state.loading = false;
    });
    builder.addCase(fetchConversations.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(fetchConversations.rejected, (state, action) => {
      state.loading = false;
    });
  }
});

export const conversationSelectors = conversationsAdapter
  .getSelectors((state: RootState) => state.conversations);
